// src/api/ticketService.js - Firebase service layer for support tickets
import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  arrayUnion,
  Timestamp
} from 'firebase/firestore';
import { db } from '../utils/firebase';

const TICKETS_COLLECTION = 'tickets';
const CLIENTS_COLLECTION = 'clients';

/**
 * Fetch client email by salonId from clients collection
 */
export const getClientEmailBySalonId = async (salonId) => {
  try {
    const clientsRef = collection(db, CLIENTS_COLLECTION);
    const q = query(clientsRef, where('salonId', '==', salonId));
    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      const clientData = snapshot.docs[0].data();
      return clientData.email || '';
    }
    return '';
  } catch (error) {
    console.error('Error fetching client email:', error);
    return '';
  }
};

/**
 * Create a new support ticket
 */
export const createTicket = async (ticketData) => {
  try {
    const now = Timestamp.now();
    const ticketDoc = {
      salonId: ticketData.salonId,
      dateCreated: serverTimestamp(),
      dateUpdatedClient: serverTimestamp(),
      dateUpdatedSupport: null,
      ticketHeader: ticketData.ticketHeader,
      ticketDescription: ticketData.ticketDescription,
      ticketStatus: 'Waiting for Support',
      ticketSeverity: ticketData.ticketSeverity,
      ticketType: ticketData.ticketType,
      clientEmail: ticketData.clientEmail,
      messages: [
        {
          sender: 'client',
          senderName: ticketData.senderName || 'Client',
          message: ticketData.ticketDescription,
          timestamp: now
        }
      ]
    };

    const docRef = await addDoc(collection(db, TICKETS_COLLECTION), ticketDoc);
    return { id: docRef.id, ...ticketDoc };
  } catch (error) {
    console.error('Error creating ticket:', error);
    throw error;
  }
};

/**
 * Get all tickets for a specific salon, ordered by dateCreated desc
 */
export const getTicketsBySalonId = async (salonId) => {
  try {
    const ticketsRef = collection(db, TICKETS_COLLECTION);
    const q = query(
      ticketsRef,
      where('salonId', '==', salonId),
      orderBy('dateCreated', 'desc')
    );
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching tickets by salonId:', error);
    throw error;
  }
};

/**
 * Get all tickets for superadmin, ordered by dateCreated desc
 */
export const getAllTickets = async () => {
  try {
    const ticketsRef = collection(db, TICKETS_COLLECTION);
    const q = query(ticketsRef, orderBy('dateCreated', 'desc'));
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching all tickets:', error);
    throw error;
  }
};

/**
 * Get a single ticket by ID
 */
export const getTicketById = async (ticketId) => {
  try {
    const ticketRef = doc(db, TICKETS_COLLECTION, ticketId);
    const ticketDoc = await getDoc(ticketRef);

    if (ticketDoc.exists()) {
      return { id: ticketDoc.id, ...ticketDoc.data() };
    }
    return null;
  } catch (error) {
    console.error('Error fetching ticket:', error);
    throw error;
  }
};

/**
 * Update ticket fields (status, severity, type)
 */
export const updateTicket = async (ticketId, updates) => {
  try {
    const ticketRef = doc(db, TICKETS_COLLECTION, ticketId);
    await updateDoc(ticketRef, updates);
    return true;
  } catch (error) {
    console.error('Error updating ticket:', error);
    throw error;
  }
};

/**
 * Add a message to a ticket's messages array.
 * Auto-updates dateUpdatedClient or dateUpdatedSupport and flips ticket status.
 */
export const addTicketMessage = async (ticketId, message, sender, senderName) => {
  try {
    const now = Timestamp.now();
    const newMessage = {
      sender,
      senderName,
      message,
      timestamp: now
    };

    const updates = {
      messages: arrayUnion(newMessage)
    };

    if (sender === 'client') {
      updates.dateUpdatedClient = serverTimestamp();
      updates.ticketStatus = 'Waiting for Support';
    } else {
      updates.dateUpdatedSupport = serverTimestamp();
      updates.ticketStatus = 'Waiting for Customer';
    }

    const ticketRef = doc(db, TICKETS_COLLECTION, ticketId);
    await updateDoc(ticketRef, updates);
    return newMessage;
  } catch (error) {
    console.error('Error adding ticket message:', error);
    throw error;
  }
};
